// common.tsx — shared layout helpers used across views.
// Ported from the prototype's common.jsx; inline styles unchanged.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './icons';
import { Button, Chip, Modal, mono, iconBtn, thStyle } from './primitives';
import { expiryState } from '@/domain/dates';
import { catName, storeName, activeStores } from '@/domain/reference';
import type { Product } from '@/domain/types';

export { catName, storeName, activeStores };

export interface Crumb {
  label: ReactNode;
  onClick?: () => void;
}

export function PageHeader({
  title,
  subtitle,
  children,
  back,
  crumbs,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  back?: { label: ReactNode; onClick: () => void };
  /** breadcrumb trail (detail pages) — supersedes the back link */
  crumbs?: Crumb[];
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: 22,
        gap: 'var(--space-5)',
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          minWidth: 0,
          flex: '1 1 360px',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-1)',
        }}
      >
        {crumbs && crumbs.length > 0 && (
          <nav
            aria-label="Breadcrumb"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
              marginBottom: 'var(--space-1)',
              fontSize: 'var(--fs-body)',
              fontWeight: 600,
            }}
          >
            {crumbs.map((c, i) => {
              const last = i === crumbs.length - 1;
              return (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {i > 0 && <Icon name="chevright" size={13} color="var(--text-disabled)" />}
                  {c.onClick && !last ? (
                    <button
                      onClick={c.onClick}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        padding: 0,
                        font: 'inherit',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {c.label}
                    </button>
                  ) : (
                    <span style={{ color: last ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                      {c.label}
                    </span>
                  )}
                </span>
              );
            })}
          </nav>
        )}
        {!crumbs && back && (
          <button
            onClick={back.onClick}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              fontSize: 'var(--fs-body)',
              fontWeight: 600,
              padding: 0,
              marginBottom: 'var(--space-1)',
              alignSelf: 'flex-start',
            }}
          >
            <Icon name="chevleft" size={16} /> {back.label}
          </button>
        )}
        <h1
          className="h3"
          style={{ margin: 0, lineHeight: 1.25, textWrap: 'pretty' } as CSSProperties}
        >
          {title}
        </h1>
        {subtitle && (
          <p className="body2" style={{ margin: 0, color: 'var(--text-secondary)' }}>
            {subtitle}
          </p>
        )}
      </div>
      <div
        style={{
          marginLeft: 'auto',
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          flex: '0 1 auto',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// search + filter bar that sits above tables
export function FilterBar({
  search,
  setSearch,
  placeholder,
  children,
}: {
  search: string;
  setSearch: (v: string) => void;
  placeholder?: string;
  children?: ReactNode;
}) {
  const [f, setF] = useState(false);
  return (
    <div
      style={{
        padding: 'var(--space-4)',
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          flex: 1,
          minWidth: 220,
          height: 40,
          background: f ? 'var(--bg-card)' : 'var(--grey-100)',
          border: `1px solid ${f ? 'var(--primary-main)' : 'var(--grey-300)'}`,
          borderRadius: 'var(--radius-md)',
          padding: '0 12px',
          color: 'var(--text-disabled)',
          transition: 'all var(--duration-fast) var(--ease)',
        }}
      >
        <Icon name="search" size={18} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setF(true)}
          onBlur={() => setF(false)}
          placeholder={placeholder}
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: 'var(--fs-emphasis)',
            color: 'var(--text-primary)',
            flex: 1,
          }}
        />
      </div>
      {children}
    </div>
  );
}

export type SegmentOption = string | { value: string; label: ReactNode; testId?: string };
// THE pick-one-of-N pill control (Wave B): round pills, primary-main active —
// the one shared implementation behind every filter-pill row (was Segment /
// compliance cpill / stocks pillStyle / Alerts typePill / NetworkStock lens).
export function Segment({
  options,
  value,
  onChange,
  ariaLabel,
  size = 'm',
}: {
  options: SegmentOption[];
  value: string;
  onChange: (v: string) => void;
  /** Accessible name for the group (role=group). */
  ariaLabel?: string;
  size?: 's' | 'm';
}) {
  return (
    <div role="group" aria-label={ariaLabel} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const id = typeof o === 'string' ? o : o.value;
        const label = typeof o === 'string' ? o : o.label;
        const testId = typeof o === 'string' ? undefined : o.testId;
        const active = value === id;
        return (
          <button
            key={id}
            type="button"
            aria-pressed={active}
            data-testid={testId}
            onClick={() => onChange(id)}
            style={{
              border: 'none',
              cursor: 'pointer',
              borderRadius: 'var(--radius-pill)',
              padding: size === 's' ? '6px 12px' : '7px 14px',
              fontSize: size === 's' ? 'var(--fs-body-sm)' : 'var(--fs-body)',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              background: active ? 'var(--primary-main)' : 'var(--grey-200)',
              // text-primary, not text-secondary: secondary on the grey-200
              // pill is 4.45:1 in light mode — a hair under AA (axe, network
              // matrix audit). Weight + fill carry the active distinction.
              // NO transition on the state flip: a crossfading fill/text pair passes
              // through sub-AA blends, and axe can sample mid-fade when the
              // data-driven default lens flips on load (network matrix audit).
              color: active ? 'var(--primary-contrast)' : 'var(--text-primary)',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// key/value detail grid used in overview tabs
export function KV({
  items,
  cols = 2,
}: {
  items: { k: ReactNode; v: ReactNode }[];
  cols?: number;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '20px 32px' }}>
      {items.map((it, i) => (
        <div key={i} style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--fs-caption)',
              fontWeight: 700,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              marginBottom: 5,
            }}
          >
            {it.k}
          </div>
          <div
            style={{
              fontSize: 'var(--fs-emphasis)',
              fontWeight: 600,
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            {it.v}
          </div>
        </div>
      ))}
    </div>
  );
}

export function SectionTitle({
  children,
  action,
  style,
}: {
  children?: ReactNode;
  action?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: '0 0 14px',
        ...style,
      }}
    >
      {/* h2, not h4: section titles sit directly under the PageHeader h1 — the
          old h4 skipped levels on every detail page (detail wave, a11y). The
          visual style is unchanged; only the semantic level moves. */}
      <h2
        style={{
          margin: 0,
          fontSize: 'var(--fs-emphasis)',
          fontWeight: 700,
          color: 'var(--text-primary)',
        }}
      >
        {children}
      </h2>
      {action}
    </div>
  );
}

/** Single-page detail layout: a sticky meta sidebar (left) beside a scrolling content
 *  column (right). Collapses to one column on narrow screens (see .piq-detail-grid).
 *  Used by the product / prescription / dispense detail pages so overview + line/batch
 *  tables + movements/audit all live on one page. */
export function DetailLayout({ sidebar, children }: { sidebar: ReactNode; children: ReactNode }) {
  return (
    <div className="piq-detail-grid">
      <aside className="piq-detail-sidebar">{sidebar}</aside>
      {/* minWidth:0 lets wide tables scroll inside the column instead of stretching the grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

/** A titled, bordered content section for the detail pages. `flush` removes body padding
 *  so an edge-to-edge table sits cleanly under the header. */
export function PanelSection({
  title,
  action,
  badge,
  id,
  flush,
  children,
}: {
  title: ReactNode;
  action?: ReactNode;
  badge?: number | string;
  id?: string;
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      style={{
        border: '1px solid var(--divider)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-card)',
        overflow: 'hidden',
        scrollMarginTop: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '12px 16px',
          borderBottom: '1px solid var(--divider)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--fs-emphasis)',
              fontWeight: 700,
              color: 'var(--text-primary)',
            }}
          >
            {title}
          </h2>
          {badge !== undefined && (
            <Chip color="default" size="s">
              {badge}
            </Chip>
          )}
        </div>
        {action}
      </div>
      <div style={{ padding: flush ? 0 : 16 }}>{children}</div>
    </section>
  );
}

/** A collapsible (accordion) section for less-frequently-used detail panels
 *  (EPS token, picking ticket, compliance). Header toggles the body open/closed. */
export function Collapsible({
  title,
  badge,
  defaultOpen = false,
  flush,
  children,
}: {
  title: ReactNode;
  badge?: number | string;
  defaultOpen?: boolean;
  flush?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      style={{
        border: '1px solid var(--divider)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-card)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: '12px 16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
        }}
      >
        <Icon
          name="chevright"
          size={15}
          color="var(--text-secondary)"
          style={{
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform var(--duration-fast) var(--ease)',
          }}
        />
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--fs-emphasis)',
            fontWeight: 700,
            color: 'var(--text-primary)',
          }}
        >
          {title}
        </h2>
        {badge !== undefined && (
          <Chip color="default" size="s">
            {badge}
          </Chip>
        )}
      </button>
      {open && (
        <div style={{ padding: flush ? 0 : 16, borderTop: '1px solid var(--divider)' }}>
          {children}
        </div>
      )}
    </section>
  );
}

/** A compact, connected horizontal stepper — dots joined by lines with labels beneath. Replaces
 *  bulky stage cards so the stepper takes one slim row and leaves room for the content below. */
export interface StageStep {
  label: string;
  done: boolean;
  active: boolean;
  /** Optional live detail under the label (timestamp, actor, count). */
  sub?: string;
}

/** Map a wizard's `labels + current index` onto StageStepper steps (a `current`
 *  equal to `labels.length` renders everything done — the review/complete state). */
export function wizardSteps(labels: string[], current: number): StageStep[] {
  return labels.map((label, i) => ({ label, done: i < current, active: i === current }));
}

export function StageStepper({ steps: rawSteps }: { steps: StageStep[] }) {
  // Callers derive done/active per step from independent status flags, which can
  // disagree (e.g. mock data with a tray assigned but `allocated` unset) and render
  // TWO purple "active" dots. Steps are order-significant, so anything before the
  // furthest active step is by definition complete — force it done.
  let pivot = -1;
  for (let i = rawSteps.length - 1; i >= 0; i--) {
    if (rawSteps[i].active) {
      pivot = i;
      break;
    }
  }
  const steps =
    pivot <= 0
      ? rawSteps
      : rawSteps.map((s, i) => (i < pivot ? { ...s, done: true, active: false } : s));
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        padding: '8px 14px',
        border: '1px solid var(--divider)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-card)',
        overflowX: 'auto',
      }}
    >
      {steps.map((s, i) => {
        const dotBg = s.done
          ? 'var(--success-main)'
          : s.active
            ? 'var(--primary-main)'
            : 'var(--grey-300)';
        return (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: i < steps.length - 1 ? 1 : 'none',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: 66,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: dotBg,
                  color: 'var(--primary-contrast)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'var(--fs-caption)',
                  fontWeight: 700,
                }}
              >
                {s.done ? '✓' : i + 1}
              </div>
              <span
                style={{
                  fontSize: 'var(--fs-caption)',
                  fontWeight: s.active ? 700 : 600,
                  // pending labels are text-secondary, not text-disabled: these
                  // are real stage names the user reads ahead to, and disabled
                  // grey is 2.73:1 on the drawer surface (axe, drawer-open audit)
                  color: s.active ? 'var(--text-primary)' : 'var(--text-secondary)',
                  marginTop: 'var(--space-1)',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.label}
              </span>
              {s.sub && (s.done || s.active) && (
                <span
                  style={{
                    fontSize: 'var(--fs-caption)',
                    color: 'var(--text-secondary)',
                    marginTop: 1,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    maxWidth: 140,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.sub}
                </span>
              )}
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  minWidth: 16,
                  margin: '0 6px',
                  marginBottom: 15,
                  background: s.done ? 'var(--success-main)' : 'var(--grey-200)',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** A slim, single-strip overview — compact label/value pairs that flow and wrap on one line (or
 *  two), with a thin coloured accent on the left. Deliberately low-profile so the table below gets
 *  the room; not a grid of big blocks. */
export function OverviewCard({ items }: { items: { k: ReactNode; v: ReactNode }[] }) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '6px 22px',
        padding: '10px 16px',
        borderLeft: '3px solid var(--primary-main)',
        border: '1px solid var(--divider)',
        borderLeftWidth: 3,
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-card)',
        marginBottom: 'var(--space-4)',
      }}
    >
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <span
            style={{
              fontSize: 'var(--fs-caption)',
              fontWeight: 700,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              // text-secondary, not text-disabled (2.73:1 on the drawer surface —
              // axe, drawer-open audit); matches the KV label style above.
              color: 'var(--text-secondary)',
              lineHeight: 1.3,
            }}
          >
            {it.k}
          </span>
          <span
            style={{ fontSize: 'var(--fs-body)', fontWeight: 600, color: 'var(--text-primary)' }}
          >
            {it.v}
          </span>
        </div>
      ))}
    </div>
  );
}

/** A row of buttons (status timeline, picking ticket, audit trail…) that each open their panel in a
 *  closable modal pop-up — so opening one never pushes the primary content down the page. Place it
 *  at the top of a detail page's content so the buttons stay visible without scrolling. */
export function PanelButtons({
  panels,
}: {
  panels: { id: string; label: string; badge?: number | string; content: ReactNode }[];
}) {
  const [open, setOpen] = useState<string | null>(null);
  if (!panels.length) return null;
  const active = panels.find((p) => p.id === open);
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginBottom: 14,
      }}
    >
      {panels.map((p) => (
        <Button
          key={p.id}
          variant="outlined"
          size="s"
          icon="fileText"
          onClick={() => setOpen(p.id)}
        >
          {p.label}
          {p.badge !== undefined ? `  ${p.badge}` : ''}
        </Button>
      ))}
      {active && (
        <Modal open onClose={() => setOpen(null)} title={active.label} width={820}>
          {active.content}
        </Modal>
      )}
    </div>
  );
}

// generic table shell
export function Table({
  cols,
  children,
  rightAlign = [],
  scrollable = false,
  loading = false,
  skeletonRows = 6,
  error = false,
  colWidths,
  density = 'comfortable',
  stickyFirstCol = false,
}: {
  cols: ReactNode[];
  children?: ReactNode;
  rightAlign?: number[];
  /** cap height and scroll internally — headers stay pinned (long lists) */
  scrollable?: boolean;
  /** render shimmer placeholder rows while the query is in flight */
  loading?: boolean;
  skeletonRows?: number;
  /** failed query: render an explanation instead of an empty-looking table */
  error?: boolean | string;
  /** per-column widths (e.g. ['40%','12%',…]). When set, the table uses fixed layout so columns
   *  fit the container and long cell content wraps instead of forcing a wide horizontal scroll. */
  colWidths?: (string | number | undefined)[];
  /** WS-A row density: 'comfortable' (default — worklists, exactly the current appearance,
   *  --row-h-comfortable) or 'compact' (reference/lookup lists, --row-h-compact). No page opts
   *  in yet; per-module adoption is later module-UX work. */
  density?: 'comfortable' | 'compact';
  /** Wave E: pin the first column while the rest scrolls horizontally, so a row
   *  never loses the thing that identifies it on a narrow screen. */
  stickyFirstCol?: boolean;
}) {
  return (
    <div
      style={
        scrollable ? { overflow: 'auto', maxHeight: 'min(68vh, 720px)' } : { overflowX: 'auto' }
      }
    >
      <table
        className={`density-${density}${stickyFirstCol ? ' piq-sticky-col' : ''}`}
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          ...(colWidths ? { tableLayout: 'fixed' } : {}),
        }}
      >
        {colWidths && (
          <colgroup>
            {cols.map((_, i) => (
              <col key={i} style={colWidths[i] ? { width: colWidths[i] } : undefined} />
            ))}
          </colgroup>
        )}
        <thead>
          <tr>
            {cols.map((h, i) => (
              <th
                key={i}
                style={{
                  ...thStyle,
                  background: 'var(--grey-200)',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  textAlign: rightAlign.includes(i) ? 'right' : 'left',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: skeletonRows }, (_, r) => (
              <tr key={r} style={{ borderBottom: '1px solid var(--divider)' }}>
                {cols.map((_, c) => (
                  <td key={c} style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <div
                      className="skeleton"
                      style={{ height: 13, width: c === 0 ? '70%' : '50%' }}
                    />
                  </td>
                ))}
              </tr>
            ))
          ) : error ? (
            <tr>
              <td
                colSpan={cols.length}
                style={{ padding: 'var(--space-5) var(--space-4)', textAlign: 'center' }}
              >
                <span
                  style={{
                    fontSize: 'var(--fs-body)',
                    color: 'var(--error-dark)',
                  }}
                >
                  {typeof error === 'string'
                    ? error
                    : "Couldn't load this list — you may not have access to the selected store."}
                </span>
              </td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}

// clickable table row
export function Row({
  children,
  onClick,
  dim,
}: {
  children?: ReactNode;
  onClick?: () => void;
  dim?: boolean;
}) {
  const [h, setH] = useState(false);
  // A row that CONTAINS its own controls (per-row action buttons) must not
  // itself carry role="button": a button with focusable descendants is an axe
  // `nested-interactive` serious violation. Such rows stay mouse/keyboard
  // clickable (tabIndex + Enter/Space) but drop the button semantics.
  const ref = useRef<HTMLTableRowElement>(null);
  const [hasControls, setHasControls] = useState(false);
  // Re-detect when the row's content changes — `children` is a fresh element tree
  // whenever the cells re-render, which is exactly when controls can appear or
  // vanish (e.g. action buttons mounting after data loads). Setting the same
  // boolean again bails out of the re-render, so this stays cheap.
  useEffect(() => {
    if (!onClick || !ref.current) return;
    setHasControls(
      !!ref.current.querySelector('button, a[href], input, select, textarea, [tabindex]'),
    );
  }, [onClick, children]);
  return (
    <tr
      ref={ref}
      onClick={onClick}
      role={onClick && !hasControls ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        borderBottom: '1px solid var(--divider)',
        cursor: onClick ? 'pointer' : 'default',
        // De-emphasis reads through the SURFACE (tinted bg + desaturated
        // content), never text opacity: the old .55 opacity blend pushed
        // text-secondary below AA (8 serious axe nodes on
        // /multistore/requests — same failure class as the PR #29 tiles).
        // grayscale(1) preserves relative luminance, so text contrast holds.
        background: h && onClick ? 'var(--grey-100)' : dim ? 'var(--row-dim-bg)' : 'transparent',
        filter: dim ? 'grayscale(1)' : undefined,
        transition: 'background var(--duration-fast) var(--ease)',
      }}
    >
      {children}
    </tr>
  );
}

// expiry cell with semantic color + chip
export function ExpiryCell({ exp }: { exp: string }) {
  const st = expiryState(exp);
  const col =
    st.tone === 'error'
      ? 'var(--error-dark)'
      : st.tone === 'warning'
        ? 'var(--warning-dark)'
        : 'var(--text-primary)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <span style={{ ...mono, color: col, fontWeight: st.tone === 'error' ? 700 : 600 }}>
        {exp}
      </span>
      {st.key === 'expired' && (
        <Chip color="error" size="s">
          Expired
        </Chip>
      )}
      {st.key === 'exp30' && (
        <Chip color="error" size="s">
          {st.days}d
        </Chip>
      )}
      {(st.key === 'exp60' || st.key === 'exp90') && (
        <Chip color="warning" size="s">
          {st.days}d
        </Chip>
      )}
    </span>
  );
}

/** Table pager. Pass `page` + `onPage` (and slice the rows to `perPage` at the
 *  call site) for real paging with accessible, end-aware chevrons; without
 *  them it stays a passive "1–{count} of {count}" readout (legacy callers
 *  render every row). */
export function Pagination({
  count,
  perPage = 10,
  page,
  onPage,
}: {
  count: number;
  perPage?: number;
  /** zero-based page index (controlled). */
  page?: number;
  onPage?: (page: number) => void;
}) {
  const controlled = page !== undefined && !!onPage;
  const p = controlled ? page : 0;
  const from = count === 0 ? 0 : p * perPage + 1;
  const to = controlled ? Math.min((p + 1) * perPage, count) : count;
  const canPrev = controlled && p > 0;
  const canNext = controlled && (p + 1) * perPage < count;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 'var(--space-2)',
        padding: '14px 20px',
      }}
    >
      <span className="caption">Rows per page: {perPage}</span>
      <span className="caption" style={{ margin: '0 8px' }}>
        {from}–{to} of {count}
      </span>
      <button
        aria-label="Previous page"
        disabled={!canPrev}
        onClick={() => canPrev && onPage?.(p - 1)}
        style={{ ...iconBtn, cursor: canPrev ? 'pointer' : 'default' }}
      >
        <Icon
          name="chevleft"
          size={18}
          color={canPrev ? 'var(--text-secondary)' : 'var(--text-disabled)'}
        />
      </button>
      <button
        aria-label="Next page"
        disabled={!canNext}
        onClick={() => canNext && onPage?.(p + 1)}
        style={{ ...iconBtn, cursor: canNext ? 'pointer' : 'default' }}
      >
        <Icon
          name="chevright"
          size={18}
          color={canNext ? 'var(--text-secondary)' : 'var(--text-disabled)'}
        />
      </button>
    </div>
  );
}

// product cell (name + form)
export function ProductCell({ p }: { p: Product }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
      <div
        style={{
          width: 38,
          height: 38,
          flex: 'none',
          borderRadius: 'var(--radius-md)',
          background: p.cd
            ? 'var(--error-soft)'
            : p.fridge
              ? 'var(--info-soft)'
              : 'var(--grey-200)',
          color: p.cd
            ? 'var(--error-dark)'
            : p.fridge
              ? 'var(--info-main)'
              : 'var(--text-secondary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={p.cd ? 'lock' : p.fridge ? 'snowflake' : 'pill'} size={18} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="subtitle2" style={{ whiteSpace: 'nowrap' }}>
          {p.name}
        </div>
        {/* only render the parts we actually know — never a dangling "·"
            (identifier policy, docs/design-patterns.md §4) */}
        {(p.pack || p.uom) && (
          <div className="caption">{[p.pack, p.uom].filter(Boolean).join(' · ')}</div>
        )}
      </div>
    </div>
  );
}

// PIQ-CR-001 §0.1 — two-value on-hand display: total physical, split Free Hold · Allocated.
export function FreeAllocCell({
  qty,
  alloc = 0,
  align = 'right',
}: {
  qty: number;
  alloc?: number;
  align?: 'right' | 'left';
}) {
  const free = Math.max(0, qty - alloc);
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: align === 'right' ? 'flex-end' : 'flex-start',
        lineHeight: 1.25,
      }}
    >
      <span
        style={{
          fontWeight: 700,
          color: qty === 0 ? 'var(--error-dark)' : 'var(--text-primary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {qty}
      </span>
      {alloc > 0 && (
        <span
          style={{
            fontSize: 'var(--fs-caption)',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            whiteSpace: 'nowrap',
          }}
        >
          Free {free} · <span style={{ color: 'var(--info-main)' }}>Alloc {alloc}</span>
        </span>
      )}
    </span>
  );
}

export function AllocChip({ qty }: { qty: number }) {
  return qty > 0 ? (
    <Chip color="info" size="s">
      {qty} allocated
    </Chip>
  ) : null;
}

// ─── Reusable column sort ──────────────────────────────────────────────────
// Click a column header to sort; click again to flip; a third click clears back to the natural
// order. Shared by the prescription queue, dispense queue and backorder register so sorting works
// identically everywhere. Accessors map a column key → a comparable value for each row.
export type SortDir = 'asc' | 'desc';
export interface SortState {
  key: string;
  dir: SortDir;
}

export function useTableSort<T>(
  rows: T[],
  accessors: Record<string, (row: T) => string | number | null | undefined>,
  initial: SortState | null = null,
) {
  const [sort, setSort] = useState<SortState | null>(initial);
  const toggle = (key: string) =>
    setSort((prev) =>
      prev?.key !== key ? { key, dir: 'asc' } : prev.dir === 'asc' ? { key, dir: 'desc' } : null,
    );
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const acc = accessors[sort.key];
    if (!acc) return rows;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      // Empty values always sort to the bottom regardless of direction.
      if (va == null || va === '') return vb == null || vb === '' ? 0 : 1;
      if (vb == null || vb === '') return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor;
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * factor;
    });
    // accessors is a stable object literal per render; depend on sort + rows only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);
  return { sorted, sort, toggle };
}

/** A clickable table header cell with a sort-direction caret. Pass as a `Table` `cols` entry. */
export function SortHeader({
  label,
  col,
  sort,
  onSort,
  align = 'left',
}: {
  label: ReactNode;
  col: string;
  sort: SortState | null;
  onSort: (key: string) => void;
  align?: 'left' | 'right';
}) {
  const active = sort?.key === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        background: 'none',
        border: 'none',
        padding: 0,
        margin: 0,
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        width: align === 'right' ? '100%' : undefined,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      }}
      aria-label={`Sort by ${typeof label === 'string' ? label : col}`}
    >
      {label}
      <span
        style={{
          fontSize: 'var(--fs-caption)',
          lineHeight: 1,
          color: active ? 'var(--primary-main)' : 'var(--text-disabled)',
        }}
      >
        {active ? (sort!.dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  );
}
